import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/components/providers/AuthProvider";
import { useAcceptClubInvitationMutation } from "@/api/clubsApi";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";

export const Route = createFileRoute("/invite/$inviteToken")({
  component: InviteAcceptPage,
});

function InviteAcceptPage() {
  const { inviteToken } = Route.useParams();
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const [acceptInvitation, { isLoading: isAccepting }] = useAcceptClubInvitationMutation();
  const [error, setError] = useState<{ message: string; type: "expired" | "not_found" | "already_accepted" | "generic" } | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // If not authenticated, redirect to login with return URL
    if (!isAuthLoading && !user) {
      // Store token in sessionStorage for retrieval after login
      sessionStorage.setItem("pendingInviteToken", inviteToken);
      navigate({
        to: "/login",
        search: { redirect: `/invite/${inviteToken}` },
      });
      return;
    }

    // If authenticated, accept the invitation
    if (user && !accepted && !error && !isAccepting) {
      handleAccept();
    }
  }, [user, isAuthLoading, inviteToken]);

  const handleAccept = async () => {
    try {
      const result = await acceptInvitation({ inviteToken }).unwrap();

      if (result.success && result.club) {
        setAccepted(true);
        // Clean up sessionStorage
        sessionStorage.removeItem("pendingInviteToken");
        // Redirect to the club
        setTimeout(() => {
          navigate({ to: "/clubs/$identifier", params: { identifier: result.club!.id } });
        }, 1500);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);

      if (errorMessage.includes("expired")) {
        setError({ message: "This invitation has expired.", type: "expired" });
      } else if (errorMessage.includes("already been accepted")) {
        setError({ message: "This invitation has already been accepted.", type: "already_accepted" });
      } else if (errorMessage.includes("not found") || errorMessage.includes("revoked")) {
        setError({ message: "This invitation could not be found.", type: "not_found" });
      } else {
        setError({ message: errorMessage || "Something went wrong. Please try again.", type: "generic" });
      }
    }
  };

  // Loading state
  if (isAuthLoading || isAccepting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-accent-primary mx-auto" />
          <p className="text-body-1 text-text-secondary">
            {isAuthLoading ? "Checking your account..." : "Accepting invitation..."}
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (accepted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4 max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
            <Icon name="check" size={28} className="text-green-500" />
          </div>
          <h1 className="text-h4 text-text-primary">You're in!</h1>
          <p className="text-body-1 text-text-secondary">
            Redirecting you to the club...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4 max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-accent-negative/10 flex items-center justify-center mx-auto">
            <Icon
              name={error.type === "expired" ? "clock" : error.type === "already_accepted" ? "user-check" : "circle-exclamation"}
              size={28}
              className={error.type === "already_accepted" ? "text-amber-500" : "text-accent-negative"}
            />
          </div>
          <h1 className="text-h4 text-text-primary">
            {error.type === "expired"
              ? "Invitation expired"
              : error.type === "already_accepted"
                ? "Already accepted"
                : error.type === "not_found"
                  ? "Invitation not found"
                  : "Something went wrong"}
          </h1>
          <p className="text-body-1 text-text-secondary">{error.message}</p>
          <Button onClick={() => navigate({ to: "/clubs" })} className="mt-4">
            Go to clubs
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
